import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { S3UploadService, PresignedUrlRequest } from 'gung-common';
import { concatMap, filter, first, forkJoin, map, Observable, of, retry, switchMap, take, tap, timeout, timer } from 'rxjs';
import {
  CatalogueGeneratorHistoryResponse,
  CatalogueGeneratorResponse,
  GenerationJobPollResponse,
  GenerationJobSubmission,
  PdfDocumentBuilderAttachmentRequest,
  PdfDocumentTemplate,
  PdfDocumentTemplateEditableFields,
  PdfDocumentTemplateGroupSettings,
  PdfDocumentTemplateListFilters,
  PdfDocumentType
} from '../../models/pdf-document-builder.model';

@Injectable({
  providedIn: 'root'
})
export class PdfDocumentBuilderService {
  private readonly http = inject(HttpClient);
  private readonly s3Upload = inject(S3UploadService);
  private readonly baseUrl = 'json/feature/pdf-document-builder';
  private readonly attachmentKeyPrefix = 'pdf-document-builder/attachments';
  private readonly attachmentDownloadValidityMinutes = 60;
  private readonly noCacheHeaders = {
    maxAge: '-1'
  };
  private readonly generationPollIntervalMs = 2500;
  private readonly generationPollCeilingMs = 10 * 60 * 1000;
  private readonly generationSubmitTimeoutMs = 8000;
  private readonly activeJobStoragePrefix = 'pdf-document-builder.active-job.';
  private readonly activeJobStaleAfterMs = this.generationPollCeilingMs * 2;

  getDocumentTypes(): Observable<PdfDocumentType[]> {
    return this.http.get<PdfDocumentType[]>(`${this.baseUrl}/document-types`, {
      headers: this.noCacheHeaders
    });
  }

  getTemplates(filters: PdfDocumentTemplateListFilters = {}): Observable<PdfDocumentTemplate[]> {
    let params = new HttpParams();

    if (filters.documentTypeId) {
      params = params.set('documentTypeId', filters.documentTypeId);
    }

    if (filters.status) {
      params = params.set('status', filters.status);
    }

    if (filters.latestOnly !== undefined) {
      params = params.set('latestOnly', String(filters.latestOnly));
    }

    return this.http.get<PdfDocumentTemplate[]>(`${this.baseUrl}/templates`, {
      headers: this.noCacheHeaders,
      params
    });
  }

  getTemplate(id: string): Observable<PdfDocumentTemplate> {
    return this.http.get<PdfDocumentTemplate>(`${this.baseUrl}/templates/${id}`, {
      headers: this.noCacheHeaders
    });
  }

  getLatestTemplateVersion(templateGroupId: string): Observable<PdfDocumentTemplate> {
    return this.http.get<PdfDocumentTemplate>(`${this.baseUrl}/templates/groups/${templateGroupId}/latest`, {
      headers: this.noCacheHeaders
    });
  }

  getTemplateVersions(templateGroupId: string): Observable<PdfDocumentTemplate[]> {
    return this.http.get<PdfDocumentTemplate[]>(`${this.baseUrl}/templates/groups/${templateGroupId}/versions`, {
      headers: this.noCacheHeaders
    });
  }

  createTemplate(template: PdfDocumentTemplateEditableFields): Observable<PdfDocumentTemplate> {
    return this.http.post<PdfDocumentTemplate>(`${this.baseUrl}/templates`, template);
  }

  createTemplateVersion(
    templateGroupId: string,
    template: PdfDocumentTemplateEditableFields
  ): Observable<PdfDocumentTemplate> {
    return this.http.post<PdfDocumentTemplate>(`${this.baseUrl}/templates/groups/${templateGroupId}/versions`, template);
  }

  updateTemplate(id: string, template: PdfDocumentTemplateEditableFields): Observable<PdfDocumentTemplate> {
    return this.http.put<PdfDocumentTemplate>(`${this.baseUrl}/templates/${id}`, template);
  }

  updateTemplateGroupSettings(
    templateGroupId: string,
    settings: PdfDocumentTemplateGroupSettings
  ): Observable<PdfDocumentTemplate> {
    return this.http.put<PdfDocumentTemplate>(
      `${this.baseUrl}/templates/groups/${templateGroupId}/settings`,
      settings
    );
  }

  getVisibleExports(filters: { documentTypeId: string; flowId?: string }): Observable<PdfDocumentTemplate[]> {
    let params = new HttpParams().set('documentTypeId', filters.documentTypeId);
    if (filters.flowId) {
      params = params.set('flowId', filters.flowId);
    }

    return this.http.get<PdfDocumentTemplate[]>(`${this.baseUrl}/exports`, {
      headers: this.noCacheHeaders,
      params
    });
  }

  renderPdf(body: {
    templateGroupId: string;
    entityIds: string[];
    params?: Record<string, unknown>;
  }): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/render/pdf`, body, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  publishTemplate(id: string): Observable<PdfDocumentTemplate> {
    return this.http.post<PdfDocumentTemplate>(`${this.baseUrl}/templates/${id}/publish`, {});
  }

  archiveTemplate(id: string): Observable<PdfDocumentTemplate> {
    return this.http.post<PdfDocumentTemplate>(`${this.baseUrl}/templates/${id}/archive`, {});
  }

  rollbackTemplate(id: string): Observable<PdfDocumentTemplate> {
    return this.http.post<PdfDocumentTemplate>(`${this.baseUrl}/templates/${id}/rollback`, {});
  }

  deleteTemplateVersion(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${id}`);
  }

  deleteTemplateGroup(templateGroupId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/groups/${templateGroupId}`);
  }

  generateCatalogue(
    conversationId: string,
    message: string,
    attachments: PdfDocumentBuilderAttachmentRequest[] = []
  ): Observable<CatalogueGeneratorResponse> {
    return this.submitGenerationJob(conversationId, message, attachments).pipe(
      tap(({ jobId }) => this.persistActiveJob(conversationId, jobId)),
      switchMap(({ jobId }) => this.pollGenerationJob(jobId)),
      tap({
        next: () => this.clearActiveJob(conversationId),
        error: () => this.clearActiveJob(conversationId)
      })
    );
  }

  uploadAttachment(file: File, conversationId: string): Observable<{ s3Key: string; url: string }> {
    const safeName = this.sanitizeFilename(file.name);
    const random = this.generateRandomString(8);
    const safeConversationId = (conversationId || 'shared').replace(/[^A-Za-z0-9_-]+/g, '_');
    const key = `${this.attachmentKeyPrefix}/${safeConversationId}/${random}/${safeName}`;
    const request: PresignedUrlRequest = {
      key,
      appendCustomerPrefix: true,
      publiclyAccessible: false
    };

    return this.s3Upload.presignedUrlUpload(request).pipe(
      first(),
      switchMap(presigned =>
        forkJoin({
          presigned: of(presigned),
          etag: this.s3Upload.uploadFile(file, presigned.url).pipe(first())
        })
      ),
      switchMap(({ presigned }) =>
        this.createDownloadUrl(presigned.s3Key, this.attachmentDownloadValidityMinutes).pipe(
          first(),
          map(downloadUrl => ({ s3Key: presigned.s3Key, url: downloadUrl }))
        )
      )
    );
  }

  private createDownloadUrl(s3Key: string, validityMinutes?: number): Observable<string> {
    const body: { key: string; validityMinutes?: number } = { key: s3Key };
    if (validityMinutes && validityMinutes > 0) {
      body.validityMinutes = validityMinutes;
    }
    return this.http
      .post<{ url: string }>(`json/s3/presigned-urls/download`, body)
      .pipe(map(response => response.url));
  }

  private sanitizeFilename(name: string): string {
    return (name || 'attachment').replace(/[^A-Za-z0-9._\- ]+/g, '_');
  }

  private generateRandomString(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters[Math.floor(Math.random() * characters.length)];
    }
    return result;
  }

  resumeGenerationJob(conversationId: string): Observable<CatalogueGeneratorResponse> | null {
    const active = this.getActiveJob(conversationId);
    if (!active) {
      return null;
    }

    return this.pollGenerationJob(active.jobId).pipe(
      tap({
        next: () => this.clearActiveJob(conversationId),
        error: () => this.clearActiveJob(conversationId)
      })
    );
  }

  clearActiveJob(conversationId: string): void {
    try {
      localStorage.removeItem(this.activeJobStoragePrefix + conversationId);
    } catch {
      // localStorage unavailable; nothing to clean up
    }
  }

  private persistActiveJob(conversationId: string, jobId: string): void {
    try {
      localStorage.setItem(
        this.activeJobStoragePrefix + conversationId,
        JSON.stringify({ jobId, submittedAt: Date.now() })
      );
    } catch {
      // localStorage unavailable / quota exceeded; resume is best-effort
    }
  }

  getActiveJob(conversationId: string): { jobId: string; submittedAt: number } | null {
    try {
      const raw = localStorage.getItem(this.activeJobStoragePrefix + conversationId);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as { jobId?: string; submittedAt?: number };
      if (!parsed?.jobId || !parsed.submittedAt) {
        this.clearActiveJob(conversationId);
        return null;
      }

      if (Date.now() - parsed.submittedAt > this.activeJobStaleAfterMs) {
        this.clearActiveJob(conversationId);
        return null;
      }

      return { jobId: parsed.jobId, submittedAt: parsed.submittedAt };
    } catch {
      return null;
    }
  }

  private submitGenerationJob(
    conversationId: string,
    message: string,
    attachments: PdfDocumentBuilderAttachmentRequest[]
  ): Observable<GenerationJobSubmission> {
    const params = new HttpParams().set('conversationId', conversationId);
    const body = { message, attachments: attachments || [] };

    return this.http
      .post<GenerationJobSubmission>(`${this.baseUrl}/agents/generate`, body, { params })
      .pipe(timeout(this.generationSubmitTimeoutMs));
  }

  private pollGenerationJob(jobId: string): Observable<CatalogueGeneratorResponse> {
    return timer(0, this.generationPollIntervalMs).pipe(
      concatMap(() =>
        this.http
          .get<GenerationJobPollResponse>(`${this.baseUrl}/agents/jobs/${jobId}`, {
            headers: this.noCacheHeaders
          })
          .pipe(retry({ count: 3, delay: 2000, resetOnSuccess: true }))
      ),
      filter(res => res.status === 'SUCCEEDED' || res.status === 'FAILED'),
      take(1),
      timeout(this.generationPollCeilingMs),
      map(res => {
        if (res.status === 'FAILED') {
          throw new Error(res.error || 'Could not generate catalogue');
        }

        const { jobId: _jobId, status: _status, ...payload } = res;
        return payload as CatalogueGeneratorResponse;
      })
    );
  }

  getCatalogueHistory(conversationId: string): Observable<CatalogueGeneratorHistoryResponse> {
    const params = new HttpParams().set('conversationId', conversationId);

    return this.http.get<CatalogueGeneratorHistoryResponse>(`${this.baseUrl}/agents/history`, {
      headers: this.noCacheHeaders,
      params
    });
  }
}
